/**
 * The player's own page: who they are, the picture next to their name, and the library of photos
 * any one of which can be the circle they play as.
 *
 * The uploads here never pass through our API server. `createUploadUrl` signs a PUT, the browser
 * sends the bytes straight to the bucket and `confirmUpload` writes the row — see
 * `use-image-upload.ts`, which is the only place that sequence lives.
 *
 * Three states are not the happy path and are drawn on purpose: a guest cannot own uploaded
 * objects and is offered an account instead of a form that would 403; a server with no bucket
 * configured refuses every procedure, which is an operator's problem and says so; and an empty
 * library is a prompt rather than a blank panel.
 */

import { DISPLAY_NAME_MAX_LENGTH, DISPLAY_NAME_MIN_LENGTH } from '@chompdome/shared/schemas';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { type ReactNode, useEffect, useState } from 'react';
import { AvatarCard } from '@/components/profile/avatar-card';
import { PowerUpsCard } from '@/components/profile/powerups-card';
import { SayingsCard } from '@/components/profile/sayings-card';
import { SkinGallery } from '@/components/profile/skin-gallery';
import { Button, buttonVariants } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { useSession } from '@/hooks/use-session';
import { trpc } from '@/lib/trpc';

export const Route = createFileRoute('/profile')({
  component: ProfilePage,
});

function ProfilePage() {
  const navigate = useNavigate();
  const { user, isLoading } = useSession();

  useEffect(() => {
    // Nobody at all is a sign-in problem: there is no profile to show a stranger.
    if (!isLoading && !user) navigate({ to: '/login' });
  }, [isLoading, user, navigate]);

  if (isLoading || !user) {
    return <Notice title="Profile">Checking your session…</Notice>;
  }

  return <Profile isGuest={user.isGuest} />;
}

function Notice({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="flex flex-1 justify-center p-4">
      <Card className="w-full max-w-md self-start">
        <CardHeader>
          <CardTitle>{title}</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">{children}</p>
        </CardContent>
      </Card>
    </div>
  );
}

function Profile({ isGuest }: { isGuest: boolean }) {
  const utils = trpc.useUtils();
  const me = trpc.profile.me.useQuery();
  // A guest is refused by every upload procedure, so the query is not made at all rather than
  // made and shown as an error.
  const library = trpc.uploads.listImages.useQuery(undefined, { enabled: !isGuest });

  const invalidateLibrary = async () => {
    await Promise.all([utils.uploads.listImages.invalidate(), utils.profile.me.invalidate()]);
  };

  const selectSkin = trpc.uploads.selectSkin.useMutation({ onSuccess: invalidateLibrary });
  // Two instances of the same procedure: the gallery and the avatar each need their own pending
  // and error state, and sharing one would put a failed skin delete under the profile picture.
  const deleteSkin = trpc.uploads.deleteImage.useMutation({ onSuccess: invalidateLibrary });
  const removeAvatar = trpc.uploads.deleteImage.useMutation({ onSuccess: invalidateLibrary });

  const images = library.data?.images ?? [];
  const avatarImage = images.find((image) => image.kind === 'avatar') ?? null;
  const skins = images.filter((image) => image.kind === 'skin');

  // `users.avatar_url` only ever holds an *approved* picture (or the one a Google sign-in
  // brought, which was never uploaded here and is not moderated here), which is what makes every
  // other reader of that column in the product safe. The owner is the one person shown an
  // unapproved one, so a pending upload is drawn from its own row; a rejected one is drawn not
  // at all, because its bytes were deleted from the bucket with the verdict.
  const publishedAvatarUrl = library.data?.avatarUrl ?? me.data?.avatarUrl ?? null;
  const avatarUrl = avatarImage?.status === 'pending' ? avatarImage.url : publishedAvatarUrl;

  return (
    <div className="flex flex-1 justify-center p-4">
      <div className="w-full max-w-4xl space-y-4 self-start">
        <h1 className="text-2xl font-black tracking-tight">Profile</h1>

        {me.error && (
          <p className="text-sm text-destructive" role="alert">
            {me.error.message}
          </p>
        )}

        <AccountCard
          displayName={me.data?.displayName ?? null}
          email={me.data?.email ?? null}
          createdAt={me.data?.createdAt ?? null}
          isGuest={isGuest}
        />

        {isGuest ? (
          <GuestUpgradeCard />
        ) : library.error ? (
          <Card>
            <CardHeader>
              <CardTitle>Photos are unavailable</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-destructive" role="alert">
                {library.error.message}
              </p>
            </CardContent>
          </Card>
        ) : (
          <>
            <AvatarCard
              avatarUrl={avatarUrl}
              avatarImageId={avatarImage?.id ?? null}
              avatarStatus={avatarImage?.status ?? null}
              isRemoving={removeAvatar.isPending}
              removeError={removeAvatar.error?.message ?? null}
              onRemove={() => {
                if (avatarImage) removeAvatar.mutate({ imageId: avatarImage.id });
              }}
            />

            <SkinGallery
              images={skins}
              selectedSkinImageId={library.data?.selectedSkinImageId ?? null}
              maxSkinImages={library.data?.maxSkinImages ?? 0}
              isLoading={library.isLoading}
              isBusy={selectSkin.isPending || deleteSkin.isPending}
              actionError={selectSkin.error?.message ?? deleteSkin.error?.message ?? null}
              onSelect={(imageId) => selectSkin.mutate({ imageId })}
              onDelete={(imageId) => deleteSkin.mutate({ imageId })}
            />

            {/* Beside the photos rather than on a screen of its own: both are things a player
                chooses once and both are refused to a guest for the same reason — a guest row is
                reclaimed by a device id anybody can send. */}
            <SayingsCard />
          </>
        )}

        <PersonalBestsCard stats={me.data?.stats ?? null} />

        {/* Beside the bests rather than with the photos: both of these are what a player has
            earned, where the cards above are what they chose. A guest is drawn it too — it is the
            one card here that says out loud what an account would get them, and it makes no query
            on their behalf. */}
        <PowerUpsCard isGuest={isGuest} />

        <StatsCard stats={me.data?.stats ?? null} />
      </div>
    </div>
  );
}

function AccountCard({
  displayName,
  email,
  createdAt,
  isGuest,
}: {
  displayName: string | null;
  email: string | null;
  createdAt: string | null;
  isGuest: boolean;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Account</CardTitle>
        <CardDescription>
          {isGuest
            ? 'A guest account, kept on this device.'
            : (email ?? 'No email address on this account.')}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {displayName === null ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : isGuest ? (
          // A guest has no rename form at all, because `profile.updateDisplayName` refuses
          // them: a guest row is reclaimed by a device id anybody can send, so a guest holding
          // a name would be squatting one out of the same unique namespace real accounts draw
          // from. The name is shown — it is what everybody in the room sees them as — beside
          // the one thing that would change it.
          <div className="space-y-1.5">
            <p className="text-sm font-medium">Username</p>
            <p className="text-sm text-muted-foreground">
              {displayName} — we picked it.{' '}
              <Link to="/register" className="font-medium text-primary hover:underline">
                Create an account to choose your own
              </Link>
              .
            </p>
          </div>
        ) : (
          <DisplayNameForm current={displayName} />
        )}
        {createdAt && (
          <p className="text-sm text-muted-foreground">
            Playing since {new Date(createdAt).toLocaleDateString()}
          </p>
        )}
      </CardContent>
    </Card>
  );
}

/** The rename that `profile.updateDisplayName` already backs; this is only the form for it. */
function DisplayNameForm({ current }: { current: string }) {
  const utils = trpc.useUtils();
  const [value, setValue] = useState(current);

  const rename = trpc.profile.updateDisplayName.useMutation({
    onSuccess: async () => {
      // A NAME IS NOT ONLY ON THIS CARD. It is drawn in the header and the avatar menu off
      // `auth.me`, on the profile card off `profile.me`, and beside a run on every board that
      // names a player — so the invalidation is the whole cache rather than the two queries
      // this form happens to know about. One `invalidate()` is one batch, which is what puts
      // the header and the card on the new name in the same tick instead of one after the
      // other.
      await utils.invalidate();
    },
  });

  const trimmed = value.trim();
  const unchanged = trimmed === current;

  return (
    <form
      className="space-y-1.5"
      onSubmit={(event) => {
        event.preventDefault();
        if (!unchanged) rename.mutate({ displayName: trimmed });
      }}
    >
      <label htmlFor="displayName" className="text-sm font-medium">
        Username
      </label>
      <div className="flex flex-wrap items-center gap-2">
        <Input
          id="displayName"
          value={value}
          onChange={(event) => setValue(event.target.value)}
          minLength={DISPLAY_NAME_MIN_LENGTH}
          maxLength={DISPLAY_NAME_MAX_LENGTH}
          autoComplete="nickname"
          className="max-w-64"
        />
        <Button type="submit" size="sm" disabled={rename.isPending || unchanged}>
          {rename.isPending ? 'Saving…' : 'Save'}
        </Button>
      </div>
      {rename.error && (
        <p className="text-sm text-destructive" role="alert">
          {rename.error.message}
        </p>
      )}
    </form>
  );
}

function GuestUpgradeCard() {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Create an account for photos and sayings</CardTitle>
        <CardDescription>
          A guest is remembered by this browser alone, which is not an identity to hang stored
          images — or moderated words — on. Sign up and your name, your stats, your photos and your
          sayings follow you between devices.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Link to="/register" className={buttonVariants()}>
          Create an account
        </Link>
      </CardContent>
    </Card>
  );
}

/** `profile.me` left-joins `player_stats`, so every figure here can legitimately be absent. */
interface ProfileStats {
  gamesPlayed: number | null;
  bestMass: number | null;
  bestPellets: number | null;
  bestSurvivalSeconds: number | null;
  totalKills: number | null;
  totalDeaths: number | null;
  totalPelletsEaten: number | null;
  totalMassGained: number | null;
  totalSplits: number | null;
  /** Deathmatch rounds won — see `player_stats.roundWins`. Zero for a classic-only player. */
  roundWins: number | null;
  duelWins: number | null;
  duelLosses: number | null;
  playtimeSeconds: number | null;
}

/**
 * The three lifetime personal bests, drawn apart from the running totals below them because they
 * are a different kind of number: a best is the high-water mark of one run and never falls, a
 * total is everything added up and only ever climbs. Mixing them in one grid is how a player
 * ends up reading "best mass" as "mass this month".
 *
 * These are lifetime figures, from `player_stats`. The same three cut by day, week, month or
 * year are the world board's job — a rollup cannot be re-cut by period, which is why
 * `/leaderboard` aggregates `match_results` instead of reading this table.
 */
function PersonalBestsCard({ stats }: { stats: ProfileStats | null }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Personal bests</CardTitle>
        <CardDescription>
          The best you have ever managed in a single run. See how it ranks on the{' '}
          <Link to="/leaderboard" className="underline underline-offset-2">
            world leaderboard
          </Link>
          .
        </CardDescription>
      </CardHeader>
      <CardContent>
        <dl className="grid grid-cols-2 gap-x-4 gap-y-3 sm:grid-cols-3">
          <Figure label="Biggest run" value={formatCount(stats?.bestMass)} />
          <Figure label="Most dots in a run" value={formatCount(stats?.bestPellets)} />
          <Figure label="Longest run" value={formatPlaytime(stats?.bestSurvivalSeconds)} />
        </dl>
      </CardContent>
    </Card>
  );
}

function StatsCard({ stats }: { stats: ProfileStats | null }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Lifetime totals</CardTitle>
      </CardHeader>
      <CardContent>
        <dl className="grid grid-cols-2 gap-x-4 gap-y-3 sm:grid-cols-3">
          <Figure label="Games played" value={formatCount(stats?.gamesPlayed)} />
          {/* Beside the games rather than beside the duels: a deathmatch round is a *game* you
              won, and a duel is a hand you won inside one. See `player_stats.roundWins`. */}
          <Figure label="Deathmatch wins" value={formatCount(stats?.roundWins)} />
          <Figure label="Chomps" value={formatCount(stats?.totalKills)} />
          <Figure label="Times eaten" value={formatCount(stats?.totalDeaths)} />
          <Figure label="Dots eaten" value={formatCount(stats?.totalPelletsEaten)} />
          <Figure label="Mass gained" value={formatCount(stats?.totalMassGained)} />
          <Figure label="Splits" value={formatCount(stats?.totalSplits)} />
          <Figure label="Duels won" value={formatCount(stats?.duelWins)} />
          <Figure label="Duels lost" value={formatCount(stats?.duelLosses)} />
          <Figure label="Time played" value={formatPlaytime(stats?.playtimeSeconds)} />
        </dl>
      </CardContent>
    </Card>
  );
}

function Figure({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="font-semibold tabular-nums">{value}</dd>
    </div>
  );
}

function formatCount(value: number | null | undefined): string {
  return value === null || value === undefined ? '—' : value.toLocaleString();
}

function formatPlaytime(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined) return '—';
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
}
